import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { InputAdornment, useTheme, Popover, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray, capitalize } from "lodash";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import EmojiPicker from "emoji-picker-react";
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon';
import 'moment/locale/es';
import moment from 'moment';
import SearchIcon from "../../assets/icon/SearchIcon";
import {
  canScheduleCloudTextAt,
  extractCloudTicketMeta,
  getCloudScheduleWindowBlockReason,
  isCloudTicketChannel,
  formatServiceWindowExpiry,
  scheduleModalWindowI18nPrefix,
} from "../../utils/serviceWindow";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
    borderRadius: 10,
    padding: 10,
    width: 100,
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  noHover: {
    minWidth: 0,
    paddingRight: 5,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#00000014",
      },
      "&:hover fieldset": {
        borderColor: "#00000014",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#00000014",
      },
    },
  },
  emojiButton: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  emojiPickerStyle: {
    padding: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: '100%',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    background: theme.palette.tabHeaderBackground,
  },
  dialogContent: {
    position: 'relative',
    minHeight: '300px',
    background: theme.palette.tabHeaderBackground,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: "0.75rem",
  },
  windowBanner: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#3d2c2c" : "#fff4f4",
    border: `1px solid ${theme.palette.error.light}`,
  },
  windowBannerTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.error.main,
    marginBottom: theme.spacing(0.5),
  },
  windowBannerBody: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
  },
}));

const ScheduleSchema = Yup.object().shape({
  body: Yup.string().min(5, "mensaje corto").required("Obligatorio"),
  contactId: Yup.number().required("Obligatorio"),
  whatsappId: Yup.number().required("Obligatorio"),
  sendAt: Yup.string()
    .required("Obligatorio")
  // .test("futureDate", "La fecha debe ser al menos 10 minutos posterior a la actual", function(value) {
  //   const currentDate = moment();
  //   const selectedDate = moment(value);
  //   return selectedDate.isAfter(currentDate.add(10, 'minutes'));
  // }),
});

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  whatsappId,
  ticketId,
  cleanContact,
  reload,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const bodyRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [contactSearchParam, setContactSearchParam] = useState("");
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [selectedContactOption, setSelectedContactOption] = useState(null);
  const [connections, setConnections] = useState([]);
  const [ticketMeta, setTicketMeta] = useState(null);

  const [initialDate] = useState(moment());

  const buildDefaultValues = () => ({
    body: "",
    contactId: contactId || null,
    whatsappId: whatsappId || null,
    sendAt: moment().add(20, 'minutes').format("YYYY-MM-DDTHH:mm"),
  });

  const [formInitialValues, setFormInitialValues] = useState(buildDefaultValues);

  const loadContactById = async (id) => {
    if (!id) return null;
    try {
      const { data } = await api.get(`/contacts/${id}`);
      const contact = {
        id: data.id,
        name: data.name,
        number: data.number,
      };
      setSelectedContactOption(contact);
      setContacts((prev) => {
        if (prev.some((c) => c.id === contact.id)) return prev;
        return [contact, ...prev];
      });
      return contact;
    } catch (err) {
      toastError(err);
      return null;
    }
  };

  const loadTicketMeta = async (resolvedTicketId, resolvedContactId) => {
    try {
      if (resolvedTicketId) {
        const { data } = await api.get(`/tickets/${resolvedTicketId}`);
        setTicketMeta(extractCloudTicketMeta(data));
        return;
      }
      if (resolvedContactId) {
        const { data } = await api.get(`/tickets/contact/${resolvedContactId}`);
        setTicketMeta(extractCloudTicketMeta(data));
        return;
      }
      setTicketMeta(null);
    } catch (err) {
      setTicketMeta(null);
    }
  };

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setContactSearchParam("");
    setContacts([]);
    setSelectedContactOption(null);
    setTicketMeta(null);

    (async () => {
      try {
        const { data: whatsappResponse } = await api.get(
          `/whatsapp?session=0&companyId=${user.companyId}`
        );

        const connectionsData = (whatsappResponse || [])
          .filter((conn) => conn.channel !== "webchat")
          .map((c) => ({ id: c.id, name: c.name, channel: c.channel }));

        if (isArray(connectionsData)) setConnections(connectionsData);

        let initialValues;
        let contactToLoad = contactId || null;
        let ticketToLoad = ticketId || null;

        if (scheduleId) {
          const { data } = await api.get(`/schedules/${scheduleId}`);
          initialValues = {
            ...buildDefaultValues(),
            body: data.body,
            contactId: data.contactId,
            whatsappId: data.whatsappId,
            sendAt: moment(data.sendAt).local().format("YYYY-MM-DDTHH:mm"),
            status: data.status,
          };
          contactToLoad = data.contactId || contactToLoad;
          ticketToLoad = data.ticketId || ticketToLoad;

          if (data.contact?.id) {
            const contact = {
              id: data.contact.id,
              name: data.contact.name,
              number: data.contact.number,
            };
            setSelectedContactOption(contact);
            setContacts([contact]);
            contactToLoad = null;
          }
        } else {
          initialValues = buildDefaultValues();
        }

        if (contactToLoad) {
          await loadContactById(contactToLoad);
        }

        await loadTicketMeta(ticketToLoad, contactToLoad);

        setFormInitialValues(initialValues);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, scheduleId, contactId, whatsappId, ticketId, user.companyId]);

  useEffect(() => {
    if (!open) return undefined;
    const resolvedContactId = selectedContactOption?.id || contactId || null;
    loadTicketMeta(ticketId || null, resolvedContactId);
    return undefined;
  }, [open, ticketId, contactId, selectedContactOption?.id]);

  useEffect(() => {
    if (!open || contactSearchParam.trim().length < 3) {
      setContactSearchLoading(false);
      return undefined;
    }

    setContactSearchLoading(true);
    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", {
          params: { searchParam: contactSearchParam.trim() },
        });
        const results = data.contacts || [];
        setContacts(results);
      } catch (err) {
        toastError(err);
      } finally {
        setContactSearchLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [contactSearchParam, open]);

  const handleClose = () => {
    onClose();
    if (cleanContact) {
      cleanContact();
    }
    setFormInitialValues(buildDefaultValues());
    setContactSearchParam("");
    setContacts([]);
    setSelectedContactOption(null);
    setTicketMeta(null);
  };

  const handleSaveSchedule = async (values) => {
    const selectedConnection =
      connections.find((c) => c.id === values.whatsappId) || null;
    const effectiveChannel = ticketMeta?.channel ?? selectedConnection?.channel ?? null;
    const sendAtUtc = moment(values.sendAt).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

    if (
      isCloudTicketChannel(effectiveChannel) &&
      !canScheduleCloudTextAt(
        effectiveChannel,
        ticketMeta?.serviceWindowExpiresAt ?? null,
        sendAtUtc,
      )
    ) {
      toast.error(i18n.t(`${scheduleModalWindowI18nPrefix(effectiveChannel)}.blocked`));
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...values,
        sendAt: moment(values.sendAt).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        ticketId,
      };

      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, {
          id: scheduleId,
          contactId: dataToSend.contactId,
          whatsappId: dataToSend.whatsappId,
          sendAt: dataToSend.sendAt,
          body: dataToSend.body,
          status: dataToSend.status,
          userId: dataToSend.userId,
        });
      } else {
        await api.post("/schedules", dataToSend);
      }
      toast.success(i18n.t("scheduleModal.success"));
      handleClose();
      if (reload) reload();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleEmojiButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEmojiPickerClose = () => {
    setAnchorEl(null);
  };

  const handleEmojiClick = (emojiObject, setFieldValue) => {
    const emoji = emojiObject.emoji || emojiObject.native || emojiObject;
    const field = bodyRef.current;

    if (field) {
      const start = field.selectionStart || 0;
      const end = field.selectionEnd || 0;
      const text = field.value || "";
      const newText = text.substring(0, start) + emoji + text.substring(end);

      setFieldValue("body", newText);
      field.focus();
      setTimeout(() => {
        field.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    }
    handleEmojiPickerClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          id="form-dialog-title"
          style={{
            background: theme.palette.tabHeaderBackground,
          }}
        >
          {`Programar mensaje`}
        </DialogTitle>

        {loading ? (
          <DialogContent className={classes.loadingContainer}>
            <CircularProgress />
          </DialogContent>
        ) : (
          <Formik
            initialValues={formInitialValues}
            enableReinitialize
            validationSchema={ScheduleSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSaveSchedule(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ touched, errors, isSubmitting, values, setFieldValue }) => {
              const isError = values.status === "ERROR";
              const selectedContact =
                selectedContactOption ||
                contacts.find((c) => c.id === values.contactId) ||
                null;
              const selectedConnection =
                connections.find((c) => c.id === values.whatsappId) || null;
              const effectiveChannel =
                ticketMeta?.channel ?? selectedConnection?.channel ?? null;
              const sendAtUtc = moment(values.sendAt)
                .utc()
                .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
              const scheduleWindowBlockReason = getCloudScheduleWindowBlockReason(
                effectiveChannel,
                ticketMeta?.serviceWindowExpiresAt ?? null,
                sendAtUtc,
              );
              const scheduleWindowBlocked = scheduleWindowBlockReason != null;
              const windowExpiryLabel = formatServiceWindowExpiry(
                ticketMeta?.serviceWindowExpiresAt,
              );

              const contactOptions =
                selectedContact &&
                !contacts.some((c) => c.id === selectedContact.id)
                  ? [selectedContact, ...contacts]
                  : contacts;

              const getContactOptionLabel = (option) => {
                if (!option) return "";
                if (option.number) {
                  return `${option.name} - ${option.number}`;
                }
                return option.name || "";
              };

              return (
                <Form>
                  <DialogContent
                    dividers
                    style={{ background: theme.palette.tabHeaderBackground }}
                  >
                    <div className={classes.multFieldLine}>
                      <Autocomplete
                        disabled={isError}
                        fullWidth
                        value={selectedConnection}
                        options={connections}
                        className={classes.textField}
                        onChange={(e, connection) => {
                          setFieldValue(
                            "whatsappId",
                            connection ? connection.id : null
                          );
                        }}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderInput={(params) => (
                          <Field
                            {...params}
                            variant="outlined"
                            as={TextField}
                            label={'Conexión'}
                            name="whatsappId"
                            error={touched.whatsappId && Boolean(errors.whatsappId)}
                          />
                        )}
                      />
                    </div>
                    <br />
                    <div className={classes.multFieldLine}>
                      <Autocomplete
                        disabled={isError}
                        fullWidth
                        value={selectedContact}
                        options={contactOptions}
                        loading={contactSearchLoading}
                        className={classes.textField}
                        clearOnBlur={false}
                        autoHighlight
                        noOptionsText={
                          contactSearchParam.trim().length < 3
                            ? "Escribe al menos 3 caracteres para buscar"
                            : "Sin resultados"
                        }
                        onChange={(e, contact) => {
                          setSelectedContactOption(contact);
                          setFieldValue(
                            "contactId",
                            contact ? contact.id : null
                          );
                        }}
                        getOptionLabel={getContactOptionLabel}
                        getOptionSelected={(option, value) =>
                          option.id === value?.id
                        }
                        renderInput={(params) => (
                          <>
                            <TextField
                              {...params}
                              variant="outlined"
                              label={i18n.t("newTicketModal.fieldLabel")}
                              name="contactId"
                              error={touched.contactId && Boolean(errors.contactId)}
                              helperText={touched.contactId && errors.contactId}
                              onChange={(e) => setContactSearchParam(e.target.value)}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon
                                      fill={theme.palette.messageIcons}
                                      height={20}
                                      width={20}
                                    />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <>
                                    {contactSearchLoading ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                            {!selectedContact &&
                              contactSearchParam.trim().length >= 3 &&
                              !contactSearchLoading && (
                                <Typography className={classes.errorText}>
                                  Por favor, seleccione un contacto válido
                                </Typography>
                              )}
                          </>
                        )}
                      />
                    </div>
                    <br />
                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        rows={9}
                        multiline={true}
                        label={i18n.t("scheduleModal.form.body")}
                        name="body"
                        error={touched.body && Boolean(errors.body)}
                        helperText={touched.body && errors.body}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        inputRef={bodyRef}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                className={classes.emojiButton}
                                onClick={handleEmojiButtonClick}
                              >
                                <InsertEmoticonIcon />
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <br />
                    <MuiPickersUtilsProvider utils={MomentUtils} locale="es">
                      <KeyboardDateTimePicker
                        disabled={isError || loading}
                        variant="inline"
                        format="DD/MM/YYYY HH:mm"
                        margin="normal"
                        fullWidth
                        label="Fecha y hora de envío"
                        value={values.sendAt}
                        style={{ marginLeft: theme.spacing(2) }}
                        onChange={(date) => {
                          if (date) {
                            const localDate = moment(date);
                            setFieldValue(
                              "sendAt",
                              localDate.format("YYYY-MM-DDTHH:mm")
                            );
                          }
                        }}
                        KeyboardButtonProps={{
                          "aria-label": "change date and time",
                        }}
                        className={classes.textField}
                        InputProps={{
                          disableUnderline: true,
                        }}
                        InputLabelProps={{
                          style: { color: "#888" },
                        }}
                        error={touched.sendAt && Boolean(errors.sendAt)}
                        helperText={touched.sendAt && errors.sendAt}
                        minDate={initialDate}
                        minDateMessage="La fecha no puede ser anterior a la actual"
                        ampm={false}
                      />
                    </MuiPickersUtilsProvider>
                    {scheduleWindowBlocked && (
                      <div className={classes.windowBanner} role="alert">
                        <Typography className={classes.windowBannerTitle}>
                          {i18n.t(
                            `${scheduleModalWindowI18nPrefix(effectiveChannel)}.title`,
                          )}
                        </Typography>
                        <Typography className={classes.windowBannerBody}>
                          {scheduleWindowBlockReason === "no_inbound"
                            ? i18n.t(
                                `${scheduleModalWindowI18nPrefix(effectiveChannel)}.bodyNoInbound`,
                              )
                            : i18n.t(
                                `${scheduleModalWindowI18nPrefix(effectiveChannel)}.bodyExpiresBeforeSend`,
                                {
                                expiry: windowExpiryLabel ?? "—",
                                },
                              )}
                        </Typography>
                      </div>
                    )}
                  </DialogContent>
                  <DialogActions
                    style={{ background: theme.palette.tabHeaderBackground }}
                  >
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      disabled={loading}
                      variant="outlined"
                      className={classes.btnWrapper}
                    >
                      {i18n.t("scheduleModal.buttons.cancel")}
                    </Button>
                    {!isError && (
                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading || scheduleWindowBlocked}
                        variant="contained"
                        className={classes.btnWrapper}
                      >
                        {loading ? (
                          <CircularProgress size={24} className={classes.buttonProgress} />
                        ) : scheduleId ? (
                          i18n.t("scheduleModal.buttons.okEdit")
                        ) : (
                          i18n.t("scheduleModal.buttons.okAdd")
                        )}
                      </Button>
                    )}
                  </DialogActions>
                  <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleEmojiPickerClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <div className={classes.emojiPickerStyle}>
                      <EmojiPicker
                        onEmojiClick={(emojiObject) => handleEmojiClick(emojiObject, setFieldValue)}
                        searchPlaceholder="Buscar emojis"
                        categories={{
                          smileys_people: 'Caras y personas',
                          animals_nature: 'Animales y naturaleza',
                          food_drink: 'Comida y bebida',
                          travel_places: 'Viajes y lugares',
                          activities: 'Actividades',
                          objects: 'Objetos',
                          symbols: 'Símbolos',
                          flags: 'Banderas'
                        }}
                      />
                    </div>
                  </Popover>
                </Form>
              );
            }}
          </Formik>
        )}
      </Dialog>
    </div>
  );
};

export default ScheduleModal;
